// import { solutionName } from "@/services/solution/solutionService";
import { applicationName as application } from "@/services/solution/solutionService";
import { pascalToHyphen } from "@/utils";

/**
 * Dynamic segments in the route path
 * @type {{moduleName: string, id: string}}
 */
const routeParams = {
  id: "id",
  moduleName: "moduleName"
};

/**
 * Solution route names
 * @type {{notImplemented: string, permissions: string, error404: string, about: string, apps: string}}
 */
const routeNames = {
  notImplemented: "NotImplemented",
  about: "About",
  apps: "Applications",
  permissions: "Permissions",
  error404: "404 Error"
};

/**
 * Build App Route Name
 * @param {String|string} appName
 */
const buildAppRouteName = appName => pascalToHyphen(appName);

/**
 * Build Application Route
 * @param {String} appName
 * @returns {{name: {String}}}
 */
const buildAppRoute = appName => {
  return { name: pascalToHyphen(appName) };
};

/**
 * Build Application route Path based on application name
 * @param {String} appName
 * @returns {string}
 */
const buildAppRoutePath = appName => {
  return process.env.BASE_URL === "/" ? `/${appName}` : `/${appName}`;
};

/**
 * Get module Details Path
 * @type {string}
 */
const moduleDetailsPath = `:${routeParams.id}`;

/**
 * Build Module route Name based on application and module route name
 * @param {String|string} appName
 * @param {String|string} moduleRouteName
 * @returns {String|string}
 */
const buildModuleRouteName = (appName, moduleRouteName) => {
  return `${appName}_${moduleRouteName}`;
};

/**
 * Build Module Route Path based on module route name
 * @param {String} moduleRouteName
 * @returns {string}
 */
const buildModuleRoutePath = moduleRouteName => {
  return pascalToHyphen(moduleRouteName);
};

/**
 * Build Module Details Route Name
 * @param {String|string} appName
 * @param {String|string} moduleRouteName
 * @returns {string|string}
 */
const buildModuleDetailsRouteName = (appName, moduleRouteName) =>
  `${buildModuleRouteName(appName, moduleRouteName)}_details`;

/**
 * Build Module Child Route Name
 * @param {String|string} appName
 * @param {String|string} moduleRouteName
 * @param {String|string} childRouteName
 * @returns {string|string}
 */
const buildModuleChildRouteName = (appName, moduleRouteName, childRouteName) =>
  `${buildModuleRouteName(appName, moduleRouteName)}_${childRouteName}`;

/**
 * build Module Sub-Details Route Name
 * @param {String} appName
 * @param {String} moduleRouteName
 * @param {String} tabRouteName
 * @returns {string}
 */
const buildModuleTabDetailsRouteName = (
  appName,
  moduleRouteName,
  tabRouteName
) => `${appName}_${moduleRouteName}_${tabRouteName}`;

/**
 * build Module Sub-Tab Details Route Name
 * @param {String} appName
 * @param {String} moduleRouteName
 * @param {String} tabRouteName
 * @param {String} subTabRouteName
 * @returns {string}
 */
const buildModuleSubTabDetailsRouteName = (
  appName,
  moduleRouteName,
  tabRouteName,
  subTabRouteName
) => `${appName}_${moduleRouteName}_${tabRouteName}_${subTabRouteName}`;

export {
  buildAppRouteName,
  buildAppRoute,
  buildAppRoutePath,
  buildModuleRouteName,
  buildModuleRoutePath,
  buildModuleDetailsRouteName,
  buildModuleTabDetailsRouteName,
  buildModuleSubTabDetailsRouteName,
  buildModuleChildRouteName
};

// const loginRoutes = Object.freeze([
//   // {
//   //   path: "/",
//   //   redirect: `/${routeNames.login}`
//   // },
//   {
//     path: `/Login`,
//     name: routeNames.login,
//     component: () => import("@/views/Login"),
//     meta: {
//       requiresAuth: false,
//       title: "Login"
//     }
//   }
// ]);

const permissionsRoutes = Object.freeze([
  {
    path: `/${pascalToHyphen(routeNames.permissions)}`,
    name: routeNames.permissions,
    component: () => import("@/views/Permissions"),
    meta: {
      requiresAuth: false,
      title: "Permissions"
    }
  }
]);

const appRoutes = Object.freeze([
  {
    path: "/",
    redirect: `/${pascalToHyphen(routeNames.apps)}`
  },
  {
    path: `/${pascalToHyphen(routeNames.apps)}`,
    name: routeNames.apps,
    component: () => import("@/views/ApplicationsPage"),
    meta: {
      requiresAuth: true,
      title: "Applications"
    }
  }
]);

const homeRoutes = Object.freeze([
  {
    path: "/",
    redirect: { name: application.CityCenter }
  },
  {
    path: `/${application.CityCenter}`,
    redirect: { name: application.CityCenter }
  },
  {
    path: buildAppRoutePath("About"),
    name: routeNames.about,
    component: () => import("@/views/About")
  }
]);

const notFoundRoutes = Object.freeze([
  {
    path: "*",
    component: () => import("@/views/Home"),
    children: [
      {
        name: routeNames.error404,
        path: "",
        component: () => import("@/views/shared/errors/NotFound")
      }
    ]
  }
]);

export {
  homeRoutes,
  notFoundRoutes,
  routeParams,
  moduleDetailsPath,
  routeNames,
  appRoutes,
  permissionsRoutes
};
